// Gains strip — a transient row of SMALL ICON BOXES pinned at the bottom of
// the screen, one box per recent gain: item gains (item icon + ×count badge),
// xp gains (skill icon — or the xp glyph + a tiny statvocab abbr chip for the
// stat track — + amount badge), level-ups (levelup glyph + new level), codex
// discoveries (beast icon, discovery-gold). Icons, not words. Boxes linger a
// few seconds then fade; a repeat gain within the window UPDATES its live
// box's badge instead of appending. Purely display: everything renders from
// served fx payloads (items_gained / xp_gained / level_up / discovered) —
// the strip never infers gains from vsj patches. Additive juice: item
// flights, floaters, the notification pane and mobile toasts all stay.

import { el } from '../kit';
import * as fx from '../fx';
import * as icons from '../icons';
import * as ui from '../ui';
import { statColor } from '../statvocab';

const LINGER_MS = 4000;
const FADE_MS = 260;
const MAX_BOXES = 8;

// Skills with a recognizable existing icon (the crafting skill-tab pattern);
// anything unlisted rides the generic xp glyph.
const SKILL_ICONS: Record<string, string> = {
  gathering: 'berries', mining: 'stone_handpick', woodcutting: 'log',
  fishing: 'fish_trout', crafting: 'stone_handaxe', smithing: 'anvil_kit_iron',
  fletching: 'arrow', cooking: 'trout_cooked', leatherworking: 'light_leather',
  tailoring: 'linen_bolt', moulding: 'clay', combat: 'tab_combat',
  running: 'ui_run', spotting: 'ui_eye',
};
// The stat track: xp glyph + the statvocab colored abbr chip (colors from
// theme --stat-* through statColor; the chip is a tiny badge, icon-first).
const STAT_ABBR: Record<string, string> = {
  strength: 'str', dexterity: 'dex', intelligence: 'int',
  vitality: 'vit', agility: 'agi',
};

interface Box {
  node: HTMLElement;
  badge: HTMLElement;
  count: number;
  timer: number;
}

export function gainsStrip(): { root: HTMLElement } {
  const root = el('div', { class: 'gains-strip' });
  const live = new Map<string, Box>();

  const drop = (key: string, box: Box, instant: boolean) => {
    live.delete(key);
    if (instant || ui.reducedMotion()) {
      box.node.remove();
      return;
    }
    box.node.classList.add('out');
    setTimeout(() => box.node.remove(), FADE_MS + 40);
  };

  const rearm = (key: string, box: Box) => {
    clearTimeout(box.timer);
    box.timer = window.setTimeout(() => drop(key, box, false), LINGER_MS);
  };

  /** Append (or refresh) the box for `key`; build() makes its icon face. */
  const push = (key: string, badgeText: string, count: number, cls: string, build: () => HTMLElement[]) => {
    const had = live.get(key);
    if (had) {
      had.count = count;
      had.badge.textContent = badgeText;
      if (!ui.reducedMotion()) {
        had.node.classList.remove('pop');
        void had.node.offsetWidth;
        had.node.classList.add('pop');
      }
      rearm(key, had);
      return;
    }
    const badge = el('span', { class: 'gain-badge', text: badgeText });
    const node = el('div', { class: `gain-box ${cls}` }, ...build(), badge);
    if (!ui.reducedMotion()) node.classList.add('pop');
    root.append(node);
    const box: Box = { node, badge, count, timer: 0 };
    live.set(key, box);
    rearm(key, box);
    // cap: oldest live box makes room
    if (live.size > MAX_BOXES) {
      const first = live.entries().next().value;
      if (first) {
        clearTimeout(first[1].timer);
        drop(first[0], first[1], true);
      }
    }
  };

  fx.onFxType('items_gained', (f: any) => {
    if (!root.isConnected) return;
    const items = f.items;
    if (!Array.isArray(items)) return;
    for (const it of items) {
      if (!it || typeof it !== 'object') continue;
      const item = String(it.item ?? '');
      const n = Number(it.count ?? 1);
      const prev = live.get('item:' + item)?.count ?? 0;
      push('item:' + item, `×${prev + n}`, prev + n, 'item', () => [icons.texture(item, 26)]);
    }
  });

  fx.onFxType('xp_gained', (f: any) => {
    if (!root.isConnected) return;
    const skill = String(f.skill ?? '');
    const n = Number(f.amount ?? 0);
    const prev = live.get('xp:' + skill)?.count ?? 0;
    push('xp:' + skill, `+${prev + n}`, prev + n, 'xp', () => {
      const abbr = STAT_ABBR[skill];
      const face = [icons.texture(SKILL_ICONS[skill] ?? 'icon_xp', 26)];
      if (abbr)
        face.push(el('span', { class: 'gain-chip', style: `color:${statColor(abbr)}`, text: abbr }));
      return face;
    });
  });

  fx.onFxType('level_up', (f: any) => {
    if (!root.isConnected) return;
    const skill = String(f.skill ?? '');
    const level = Number(f.level ?? 0);
    push('lvl:' + skill, String(level), level, 'lvl', () => {
      const abbr = STAT_ABBR[skill];
      const face = [icons.texture('icon_levelup', 26)];
      if (abbr)
        face.push(el('span', { class: 'gain-chip', style: `color:${statColor(abbr)}`, text: abbr }));
      else if (SKILL_ICONS[skill])
        face.push(el('span', { class: 'gain-chip gain-chip-icon' }, icons.texture(SKILL_ICONS[skill], 12)));
      return face;
    });
  });

  fx.onFxType('discovered', (f: any) => {
    if (!root.isConnected) return;
    const icon = String(f.icon ?? '');
    if (!icon) return;
    push('disc:' + icon, '', 0, 'disc', () => [icons.texture(icon, 26)]);
  });

  return { root };
}
